/**
 * 同意書公開リンク 手動発行ダイアログ (v3.2.26+)。
 *
 * 2 モード:
 *   - 「商談に紐づけて発行」: 商談を選択 → メール送信 + 商談タイムラインに記録
 *   - 「プレビュー用 (商談なし)」: 商談に紐付かない動作確認用リンク
 */
import { Loader2, Mail, TestTube2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useIssueConsentPublicLink } from '@/hooks/useConsentDocuments';
import { useDeals } from '@/hooks/useDeals';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  consentDocumentId: string;
}

export function IssueConsentLinkDialog({
  open,
  onOpenChange,
  consentDocumentId,
}: Props) {
  const issue = useIssueConsentPublicLink();
  const { data: deals } = useDeals();
  const [mode, setMode] = useState<'deal' | 'preview'>('deal');
  const [dealQuery, setDealQuery] = useState('');
  const [selectedDealId, setSelectedDealId] = useState<string>('');

  const filteredDeals = useMemo(() => {
    const list = deals ?? [];
    if (!dealQuery.trim()) return list.slice(0, 30);
    const q = dealQuery.toLowerCase();
    return list
      .filter(
        (d) =>
          d.title?.toLowerCase().includes(q) ||
          d.id.toLowerCase().includes(q) ||
          d.primaryCategory?.toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [deals, dealQuery]);

  const handleIssue = async () => {
    if (mode === 'deal' && !selectedDealId) {
      toast.error('商談を選択してください');
      return;
    }
    try {
      const r = await issue.mutateAsync({
        dealId: mode === 'deal' ? selectedDealId : null,
        consentDocumentId,
      });
      if (mode === 'preview') {
        toast.success('プレビュー用リンクを発行しました');
      } else if (r.mailSent) {
        toast.success('公開リンクを発行＋メール送信しました');
      } else {
        toast.success(
          `公開リンクを発行しました (メール: ${r.mailReason ?? '送信なし'})`,
        );
      }
      onOpenChange(false);
      setSelectedDealId('');
      setDealQuery('');
      setMode('deal');
    } catch (e) {
      toast.error(`発行失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>公開リンクを発行</DialogTitle>
          <DialogDescription>
            商談を指定して送付するか、商談に紐付かないプレビュー用リンクを発行できます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* モード切替 */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('deal')}
              className={`flex-1 text-left text-xs p-2.5 rounded border transition-colors ${
                mode === 'deal'
                  ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                  : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              <div className="flex items-center gap-1.5 font-medium mb-1">
                <Mail className="size-3.5" />
                商談に紐づけて発行
              </div>
              <div className="text-[10px] text-[var(--text-muted)]">
                メール送信 + 商談タイムラインに記録
              </div>
            </button>
            <button
              type="button"
              onClick={() => setMode('preview')}
              className={`flex-1 text-left text-xs p-2.5 rounded border transition-colors ${
                mode === 'preview'
                  ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                  : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              <div className="flex items-center gap-1.5 font-medium mb-1">
                <TestTube2 className="size-3.5" />
                プレビュー用
              </div>
              <div className="text-[10px] text-[var(--text-muted)]">
                商談なし、動作確認用。Deal.stage は動かない
              </div>
            </button>
          </div>

          {/* 商談検索＋選択 (deal モードのみ) */}
          {mode === 'deal' && (
            <div className="space-y-2">
              <div>
                <Label className="text-xs">商談を検索</Label>
                <Input
                  value={dealQuery}
                  onChange={(e) => setDealQuery(e.target.value)}
                  placeholder="タイトル / カテゴリで検索…"
                />
              </div>
              <div className="max-h-60 overflow-y-auto border border-[var(--border)] rounded-sm">
                {filteredDeals.length === 0 ? (
                  <p className="text-xs text-[var(--text-muted)] italic p-3">
                    該当する商談がありません。
                  </p>
                ) : (
                  filteredDeals.map((d) => (
                    <label
                      key={d.id}
                      className={`flex items-start gap-2 p-2 cursor-pointer border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-hover)] ${
                        selectedDealId === d.id
                          ? 'bg-[var(--accent-subtle)]'
                          : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="deal"
                        checked={selectedDealId === d.id}
                        onChange={() => setSelectedDealId(d.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">
                          {d.title || '(タイトル未設定)'}
                        </div>
                        <div className="text-[10px] text-[var(--text-muted)] truncate">
                          stage: {d.stage} / カテゴリ: {d.primaryCategory ?? '-'}
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={issue.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleIssue()}
            disabled={issue.isPending || (mode === 'deal' && !selectedDealId)}
          >
            {issue.isPending ? (
              <Loader2 className="size-4 mr-1 animate-spin" />
            ) : null}
            {mode === 'deal' ? '発行＋メール送信' : 'プレビュー用リンク発行'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
